import { CaseStudy } from '@/lib/dto/case-studies/case-studies';
import Image from 'next/image';
import Link from 'next/link';

interface HorizontalCaseStudyCardProps {
    caseStudy: CaseStudy;
}

export default function HorizontalCaseStudyCard({ caseStudy }: HorizontalCaseStudyCardProps) {
    return (
        <Link href={caseStudy.link} className="block">
            {/* Mobile View - Grid Card Style */}
            <div className="lg:hidden rounded-xl border border-gray-200 overflow-hidden transition-all duration-300 hover:border-gray-300 cursor-pointer h-full">
                {/* Image */}
                <div className="relative h-48 sm:h-56 md:h-64 w-full">
                    <Image
                        src={caseStudy.coverImage}
                        alt={`${caseStudy.title} Cover`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                    />
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 leading-tight line-clamp-2">
                        {caseStudy.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                        {caseStudy.challenge}
                    </p>

                    {/* Capsule metadata */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                                {caseStudy.category}
                            </span>
                            <span className="text-xs text-gray-500 font-medium">
                                {caseStudy.duration}
                            </span>
                        </div>
                        <span className="text-xs text-gray-400">
                            {caseStudy.industry}
                        </span>
                    </div>
                </div>
            </div>

            {/* Desktop View - Horizontal Card Style */}
            <div className="hidden lg:block bg-white rounded-xl shadow-lg px-4 sm:px-6 md:px-8 lg:px-10 py-6 sm:py-8 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer h-90">
                <div className="flex flex-col lg:flex-row h-full">
                    {/* Left column */}
                    <div className="lg:w-4/5 lg:pr-12 flex flex-col justify-center">
                        {/* Title */}
                        <h3 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-semibold text-gray-900 mb-3 sm:mb-4 leading-tight">
                            {caseStudy.title}
                        </h3>
                        {/* Description */}
                        <p className="text-xs sm:text-sm line-clamp-3 lg:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6">
                            {caseStudy.challenge}
                        </p>
                        {/* Simple chips layout */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                                    {caseStudy.category}
                                </span>
                                <span className="text-xs text-gray-500 font-medium">
                                    {caseStudy.duration}
                                </span>
                            </div>
                            <span className="text-xs text-gray-400">
                                {caseStudy.industry}
                            </span>
                        </div>
                    </div>
                    {/* Right column - Photo */}
                    <div className="lg:w-1/2 py-4 sm:py-6 px-2 sm:px-4 relative mt-4 lg:mt-0 h-full">
                        <Image
                            src={caseStudy.coverImage}
                            alt={`${caseStudy.title} Cover`}
                            fill
                            className="rounded-lg overflow-clip object-cover"
                        />
                    </div>
                </div>
            </div>
        </Link>
    );
} 